import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { BlacklistUploadVariant } from 'features/co-pilot/campaign-builder/campaign-builder.types';
import { LinkedinAccount } from 'features/co-pilot/linkedin-accounts/linkedin-accounts.types';
import PaiLink from 'features/ui/base-components/pai-link';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiRadioGroup from 'features/ui/base-components/pai-radio-group';
import PaiTextField from 'features/ui/base-components/pai-text-field';
import _ from 'lodash';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { wz } from '../campaign-wizard/components/wizard-tokens';
import { WizGradientButton } from '../campaign-wizard/components/wizard-ui';
import AddIndividualItems from './add-individual-items';
import BlacklistFileUpload from './blacklist-file-upload';
import { Blacklist_New, BlacklistTypes } from './company-blacklist.types';
import SelectCompanyItems from './select-company-items';

interface Props {
  open: boolean;
  /** `wizard`: Kopf und Buttons im Stil der Wizard-Dialoge (Inhalt bleibt derselbe). Standard: Blacklist-Verwaltung. */
  variant?: 'default' | 'wizard';
  loading: boolean;
  blacklist?: Blacklist_New | null;

  blacklistName: string;

  blacklistType: BlacklistTypes;
  blacklistUploadVariant: BlacklistUploadVariant;

  individualItemsTextField: string;
  individualItemsTextFieldError: true | null;
  individualItemsList: string[];

  fileColumnSelection: {
    columns: { text: string; type: string }[];
    data: string[][];
    fileNames: string[];
    hasTableHeading: boolean;
  };
  blacklistItems: { url?: string; company?: string }[];
  linkedinAccounts: LinkedinAccount[];
  selectedLinkedinAccount: string;
  companyQuery: string;
  companyOptions: string[][];
  selectedCompanies: string[][];
  companyLoading: boolean;
  companyIsEmpty: boolean;

  handleBlacklistNameChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleBlacklistTypeChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleBlacklistUploadVariantChange(event: React.ChangeEvent<HTMLInputElement>): void;

  onFileDrop(acceptedFiles: any): void;

  handleColumnTypeChange(idx: number): (event: React.ChangeEvent<HTMLInputElement>) => void;

  handleHasTableHeadingChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleAcceptColumns(): void;

  handleResetBlacklistItems(): void;

  handleResetFileColumnSelection(): void;

  handleTextFieldChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleAddIndividualItem(): void;

  handlePasteIndividualItem(event: React.ClipboardEvent): void;

  handleRemoveIndividualItem(idx: number): () => void;
  handleLinkedinAccountChange(event: React.ChangeEvent<HTMLInputElement>): void;
  handleCompanyQueryChange(event: React.ChangeEvent<HTMLInputElement>): void;
  searchCompanyKeyUp(): void;
  searchCompanyKeyDown(): void;
  handleToggleCompany(value: string): () => void;
  handleRemoveCompany(value: string): () => void;

  handleClose(): void;

  handleCreateGlobalBlacklist(): void;

  handleUpdateBlacklistItems(): void;
}

const AddBlacklistDialog: React.FC<Props> = ({
  open,
  variant = 'default',
  loading,
  blacklist,
  blacklistName,
  blacklistType,
  blacklistUploadVariant,
  individualItemsTextField,
  individualItemsTextFieldError,
  individualItemsList,
  fileColumnSelection,
  blacklistItems,
  linkedinAccounts,
  selectedLinkedinAccount,
  companyQuery,
  companyOptions,
  selectedCompanies,
  companyLoading,
  companyIsEmpty,
  handleBlacklistNameChange,
  handleBlacklistTypeChange,
  handleBlacklistUploadVariantChange,
  onFileDrop,
  handleColumnTypeChange,
  handleHasTableHeadingChange,
  handleAcceptColumns,
  handleResetBlacklistItems,
  handleResetFileColumnSelection,
  handleTextFieldChange,
  handleAddIndividualItem,
  handlePasteIndividualItem,
  handleRemoveIndividualItem,
  handleLinkedinAccountChange,
  handleCompanyQueryChange,
  searchCompanyKeyUp,
  searchCompanyKeyDown,
  handleToggleCompany,
  handleRemoveCompany,
  handleClose,
  handleCreateGlobalBlacklist,
  handleUpdateBlacklistItems,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const wizard = variant === 'wizard';
  const createDisabled =
    !blacklistName.trim() || (_.isEmpty(individualItemsList) && _.isEmpty(blacklistItems) && _.isEmpty(selectedCompanies));

  const getUploadSection = () => {
    switch (blacklistUploadVariant) {
      case BlacklistUploadVariant.FILE:
        return (
          <section className={classes.section}>
            <Stack gap={2}>
              <Stack direction={'row'} alignItems={'center'} gap={2}>
                <CheckCircleIcon className={classes.icon} />
                <Typography className={classes.boldText}>
                  {blacklistType === BlacklistTypes.CONTACT
                    ? t('uploadContactList')
                    : blacklistType === BlacklistTypes.COMPANY
                    ? t('uploadCompanyList')
                    : ''}
                </Typography>
              </Stack>
              <Typography className={classes.highlightText} ml={'36px'}>
                {blacklistType === BlacklistTypes.CONTACT ? (
                  <Trans i18nKey={'contactBlacklistFileStructureDescription'}>
                    For our blacklist to function properly, please include the <strong>LinkedIn URL</strong> of each individual contact&apos;s profile
                    in your file. This allows us to accurately identify them. All other fields are optional.
                  </Trans>
                ) : (
                  <Trans i18nKey={'companyBlacklistFileStructureDescription'}>
                    For our blacklist to work effectively, please make sure your file includes the <strong>name of each company</strong> you want to
                    blacklist. All other fields are optional.
                  </Trans>
                )}
              </Typography>
              <div className={classes.controls}>
                <BlacklistFileUpload
                  blacklistType={blacklistType}
                  columns={fileColumnSelection.columns}
                  data={fileColumnSelection.data}
                  fileName={fileColumnSelection.fileNames.join(', ')}
                  hasTableHeading={fileColumnSelection.hasTableHeading}
                  blacklistItems={blacklistItems}
                  onFileDrop={onFileDrop}
                  handleColumnTypeChange={handleColumnTypeChange}
                  handleHasTableHeadingChange={handleHasTableHeadingChange}
                  handleAcceptColumns={handleAcceptColumns}
                  handleResetBlacklistItems={handleResetBlacklistItems}
                  handleResetFileColumnSelection={handleResetFileColumnSelection}
                />
              </div>
            </Stack>
          </section>
        );

      case BlacklistUploadVariant.INDIVIDUAL:
        return (
          <section className={classes.section}>
            <Stack gap={2}>
              <Stack direction={'row'} alignItems={'center'} gap={2}>
                <CheckCircleIcon className={classes.icon} />
                <Typography className={classes.boldText}>
                  {blacklistType === BlacklistTypes.CONTACT
                    ? t('typeInOrPasteContactLinkedInURL')
                    : blacklistType === BlacklistTypes.COMPANY
                    ? t('typeInOrPasteCompanyNames')
                    : ''}
                </Typography>
              </Stack>
              <div className={classes.controls}>
                <AddIndividualItems
                  textfield={individualItemsTextField}
                  textFieldError={individualItemsTextFieldError}
                  list={individualItemsList}
                  blacklistType={blacklistType}
                  handleTextFieldChange={handleTextFieldChange}
                  handleAddIndividualItem={handleAddIndividualItem}
                  handlePasteIndividualItem={handlePasteIndividualItem}
                  handleRemoveIndividualItem={handleRemoveIndividualItem}
                />
              </div>
            </Stack>
          </section>
        );

      case BlacklistUploadVariant.LINKEDIN_COMPANY_PICKER:
        return (
          <section className={classes.section}>
            <Stack gap={2}>
              <Stack direction={'row'} alignItems={'center'} gap={2}>
                <CheckCircleIcon className={classes.icon} />
                <Typography className={classes.boldText}>{t('addCompanies')}</Typography>
              </Stack>
              <div className={classes.controls}>
                <SelectCompanyItems
                  linkedinAccounts={linkedinAccounts}
                  selectedLinkedinAccount={selectedLinkedinAccount}
                  companyQuery={companyQuery}
                  companyOptions={companyOptions}
                  selectedCompanies={selectedCompanies}
                  companyLoading={companyLoading}
                  companyIsEmpty={companyIsEmpty}
                  handleLinkedinAccountChange={handleLinkedinAccountChange}
                  handleCompanyQueryChange={handleCompanyQueryChange}
                  searchCompanyKeyUp={searchCompanyKeyUp}
                  searchCompanyKeyDown={searchCompanyKeyDown}
                  handleToggleCompany={handleToggleCompany}
                  handleRemoveCompany={handleRemoveCompany}
                />
              </div>
            </Stack>
          </section>
        );

      default:
        return <></>;
    }
  };

  return (
    <Dialog
      open={open}
      classes={{ paper: classes.paper, container: classes.container }}
      fullWidth={true}
      maxWidth={'md'}
      transitionDuration={wizard ? undefined : 0}
    >
      {wizard ? (
        <DialogTitle sx={{ fontSize: 20, fontWeight: 700 }}>
          {_.isNil(blacklist)
            ? t('createBlacklist')
            : t('addMoreItems', { item: blacklistType === BlacklistTypes.COMPANY ? t('companies') : t('contacts') })}
        </DialogTitle>
      ) : (
        <DialogTitle className={classes.title}>
          <Typography className={classes.titleText}>
            {_.isNil(blacklist)
              ? t('createBlacklist')
              : t('addMoreItems', { item: blacklistType === BlacklistTypes.COMPANY ? t('companies') : t('contacts') })}
          </Typography>
        </DialogTitle>
      )}
      <DialogContent className={classes.content}>
        <Stack>
          {_.isNil(blacklist) ? (
            <>
              <section className={classes.section}>
                <Stack gap={2}>
                  <Stack direction={'row'} alignItems={'center'} gap={2}>
                    <CheckCircleIcon className={classes.icon} />
                    <Typography className={classes.boldText}>{t('enterBlacklistTopicName')}</Typography>
                  </Stack>
                  <div className={classes.controls}>
                    <PaiTextField placeholder={t('enterBlacklistTopicName')} value={blacklistName} onChange={handleBlacklistNameChange} />
                  </div>
                </Stack>
              </section>
              <section className={classes.section}>
                <Stack gap={2}>
                  <Stack direction={'row'} alignItems={'center'} gap={2}>
                    <CheckCircleIcon className={classes.icon} />
                    <Typography className={classes.boldText}>{t('selectBlacklistType')}</Typography>
                  </Stack>
                  <div className={classes.controls}>
                    <PaiRadioGroup
                      value={blacklistType}
                      items={[
                        { label: t('blacklistBasedOnContacts'), value: BlacklistTypes.CONTACT },
                        { label: t('blacklistBasedOnCompanies'), value: BlacklistTypes.COMPANY },
                      ]}
                      row={true}
                      onChange={handleBlacklistTypeChange}
                    />
                  </div>
                </Stack>
              </section>
            </>
          ) : null}
          <section className={classes.section}>
            <Stack gap={2}>
              <Stack direction={'row'} alignItems={'center'} gap={2}>
                <CheckCircleIcon className={classes.icon} />
                <Typography className={classes.boldText}>
                  {t('uploadOrTypeItems', { item: blacklistType === BlacklistTypes.CONTACT ? t('contacts') : t('companies') })}
                </Typography>
              </Stack>
              <div className={classes.controls}>
                <PaiRadioGroup
                  value={blacklistUploadVariant}
                  items={[
                    { label: t('uploadFileOption'), value: BlacklistUploadVariant.FILE },
                    {
                      label: t('typeInIndividualOption', {
                        item: blacklistType === BlacklistTypes.CONTACT ? t('contacts') : t('companies'),
                      }),
                      value: BlacklistUploadVariant.INDIVIDUAL,
                    },
                    ...(blacklistType === BlacklistTypes.COMPANY
                      ? [{ label: t('addCompanies'), value: BlacklistUploadVariant.LINKEDIN_COMPANY_PICKER }]
                      : []),
                  ]}
                  row={true}
                  onChange={handleBlacklistUploadVariantChange}
                />
              </div>
            </Stack>
          </section>
          {getUploadSection()}
        </Stack>
      </DialogContent>
      <DialogActions className={classes.actions} sx={wizard ? { padding: '0 24px 20px', gap: '12px' } : undefined}>
        {wizard ? (
          <>
            <button type="button" className={classes.wizCancel} onClick={handleClose}>
              {t('cancel')}
            </button>
            {_.isNil(blacklist) ? (
              <WizGradientButton onClick={handleCreateGlobalBlacklist} disabled={createDisabled} loading={loading}>
                {t('createBlacklist')}
              </WizGradientButton>
            ) : (
              <WizGradientButton
                onClick={handleUpdateBlacklistItems}
                disabled={_.isEmpty(individualItemsList) && _.isEmpty(blacklistItems) && _.isEmpty(selectedCompanies)}
                loading={loading}
              >
                {t('update')}
              </WizGradientButton>
            )}
          </>
        ) : (
          <PaiLink text={t('cancel')} size={'sm'} underline={'always'} action={handleClose} />
        )}
        {wizard ? null : _.isNil(blacklist) ? (
          <PaiButton
            text={t('createBlacklist')}
            border={'rounded'}
            size={'small'}
            disabled={!blacklistName.trim() || (_.isEmpty(individualItemsList) && _.isEmpty(blacklistItems) && _.isEmpty(selectedCompanies))}
            loading={loading}
            action={handleCreateGlobalBlacklist}
          />
        ) : (
          <PaiButton
            text={t('update')}
            border={'rounded'}
            size={'small'}
            disabled={_.isEmpty(individualItemsList) && _.isEmpty(blacklistItems) && _.isEmpty(selectedCompanies)}
            loading={loading}
            action={handleUpdateBlacklistItems}
          />
        )}
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  title: {
    background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  container: {
    maxHeight: '960px',
  },
  content: {
    padding: '0px 24px 24px 24px !important',
    whiteSpace: 'pre-wrap',
  },
  section: {
    padding: '24px 0',
    borderBottom: `1px solid ${theme.palette.greys.light}`,
  },
  icon: {
    fontSize: theme.typography.pxToRem(20),
    color: theme.palette.primary.main,
  },
  boldText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
  },
  highlightText: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.primary.main,
  },
  controls: {
    marginLeft: 'calc(20px + 16px)',
  },
  wizCancel: {
    height: 40,
    padding: '0 20px',
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 600,
    color: wz.primary,
    background: '#FFF',
    border: `1.5px solid ${wz.primary}`,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0 24px 20px 24px',
  },
}));

export default AddBlacklistDialog;
